/* Core & libraries */
import React, {useEffect, useRef, useState} from "react";
import { useTheme } from "react-jss";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { ErrorMessage, Field, Formik } from "formik";
import {Row, Col, OverlayTrigger, Tooltip} from "react-bootstrap";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";

/* Helpers, constants, custom methods, internal libraries */
import {
  useValidationService,
  createValidationPolicyYupSchema
} from "../../../resources/validations/validations";
import { usePeopleService } from "../../../services/peopleService";
import { useHasPermission } from "../../../hooks/useHasPermission";
import { useUserContext } from "../../../contexts/userContext";
import { useAnalytics } from "../../../hooks/useAnalytics";
import { LEAVE_COMPANY } from "../../../constants/routes";
import { PAGE_TITLES } from "../../../constants/appConstants";
import { getTripitLink } from "../../../helpers/general";

/* Components */
import UCButton from "../../buttons";
import UCCheckbox from "../../uccheckbox";
import LeaveCompanyModal from "../../modals/leaveCompanyModal";
import WarningModal from "../../modals/warningModal/index.js";
import NotificationPreference from "../../notificationPreference";
import ProfileSkeleton from "../../skeletonStructureProfile";
import { ReactComponent as UserDeleteIcon } from "../../../resources/images/user-x.svg";
import { ReactComponent as DeleteUserIconRed } from "../../../resources/images/user-icon-red.svg";
import { ReactComponent as HelpIcon } from "../../../resources/images/help.svg";
import { ReactComponent as AALogoSmall } from "../../../resources/images/aa-logo-small-no-text.svg";
import { ReactComponent as EditEmailSvg } from '../../../resources/images/edit.svg';
import UpdateEmailModal from "../../modals/updateEmailModal";


/* Styles */
import styles from "./styles.js"
import generalStyles from "../../../resources/styles/general";


const messages = defineMessages({
  firstNamePlaceholder: {
    id: 'traveler.details.firstNamePlaceholder'
  },
  lastNamePlaceholder: {
    id: 'traveler.details.lastNamePlaceholder'
  },
  emailPlaceholder: {
    id: 'traveler.details.emailPlaceholder'
  },
  aadvantagePlaceholder: {
    id: 'traveler.details.aadvantagePlaceholder'
  },
})

const ProfileTab = ({travelerDetails}) => {
  const theme = useTheme();
  const classes = styles({theme});
  const general = generalStyles({ theme });
  const intl = useIntl();
  const navigate = useNavigate();

  const formRef = useRef(null);

  const { travelerDetailsMockedApiValidationObj } = useValidationService()
  const [travelerSchema, setTravelerSchema] = useState({});

  const [showLeaveCompanyModal, setShowLeaveCompanyModal] = useState(false);
  const [showLeaveCompanyAuthUserModal, setShowLeaveCompanyAuthUserModal] = useState(false);
  const [checkboxValues, setCheckboxValues] = useState({
    isTripLinkAccount: false,
    marketingBusinessSummary: false,
    marketingPromotions: false,
    marketingNewsAndOffers: false
  })
  const [showTooltip, setShowTooltip] = useState(false);
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false);

  const { updatePeople, leaveCompany } = usePeopleService();
  const { checkPermission } = useHasPermission();
  const userDetails = useUserContext();
  const { initAnalytics } = useAnalytics();

  useEffect(() => {
    setCheckboxValues({
      isTripLinkAccount: travelerDetails?.triplink,
      marketingBusinessSummary: travelerDetails?.marketingPreferences?.marketingBusinessSummary,
      marketingPromotions: travelerDetails?.marketingPreferences?.marketingPromotions,
      marketingNewsAndOffers: travelerDetails?.marketingPreferences?.marketingNewsAndOffers
    })
  }, [travelerDetails])

  useEffect(() => {
    (async () => {
      const initUtagData = {
        channel: 'content',
        pageName: PAGE_TITLES["profile"]
      };
      initAnalytics(initUtagData, 'init call', 'view')

      //TODO: api call to get validations //simpleMockedApiValidationObj
      const result = createValidationPolicyYupSchema(travelerDetailsMockedApiValidationObj);
      setTravelerSchema(result)
    })();
  }, []);

  const handleCheckbox = (checkboxKey) => {
    setCheckboxValues({
      ...checkboxValues,
      [checkboxKey]: !checkboxValues[checkboxKey]
    })
  }

  const goBack = () => {
    setCheckboxValues({
      isTripLinkAccount: travelerDetails?.triplink,
      marketingBusinessSummary: travelerDetails?.marketingPreferences?.marketingBusinessSummary,
      marketingPromotions: travelerDetails?.marketingPreferences?.marketingPromotions,
      marketingNewsAndOffers: travelerDetails?.marketingPreferences?.marketingNewsAndOffers
    })
    // navigate(-1);
  }

  const leaveAction = async () => {
    const response = await leaveCompany({
      entityID: travelerDetails?.userEntityId,
    })
    if (response) {
      setShowLeaveCompanyModal(false)
      navigate('../' + LEAVE_COMPANY);
    }
  }

  const handleFormSubmit = () => {
    if (formRef.current) {
      formRef.current.handleSubmit()
    }
  }

  const submitForm = async (values) => {
    await updatePeople({
      userId: travelerDetails?.userEntityId,
      roleId: values?.roleId,
      groupEntityId: values?.groupId,
      isTripLinkAccount: checkboxValues.isTripLinkAccount,
      marketingBusinessSummary: checkboxValues.marketingBusinessSummary,
      marketingPromotions: checkboxValues.marketingPromotions,
      marketingNewsAndOffers: checkboxValues.marketingNewsAndOffers
    })
  }

  const handleMouseEnter = () => {
    setShowTooltip(true);
  };

  const handleMouseLeave = () => {
    setShowTooltip(false);
  };

  return (
    <div className={classes.mainFormDiv}>
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="traveler.details.title.AA"/>
          </div>
          <div className={classes.description}>
            <FormattedMessage id="traveler.details.subtitle"/>
          </div>
        </div>
      </div>
      <div className={classes.dividerContainer}>
        <hr className={classes.border}/>
      </div>
      {Object.keys(travelerDetails).length === 0 ? (<ProfileSkeleton />) : (
        <>
         {
        travelerDetails?.membershipNumber && 
        <Formik
          innerRef={formRef}
          initialValues={{            
            aadvantage: `#${travelerDetails?.membershipNumber}`,
            firstName: travelerDetails?.firstName,
            lastName: travelerDetails?.lastName,            
            roleId: travelerDetails?.companyInfo?.roleId,
            groupId: travelerDetails?.companyInfo?.groupEntityId,
            emailAddress: travelerDetails?.emailAddress,
          }}
          validationSchema={travelerSchema}
          onSubmit={(values, { setSubmitting }) => {
            submitForm(values, setSubmitting);
          }}
        >
          {({
              values,
              errors,
              touched,
              setFieldTouched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue
            }) => {
            return (
              <>
                <form onSubmit={handleSubmit}>
                  <div className="row">
                    <div className="col-12 col-xl-4">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.personal"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.descr2"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={classes.formContainer}>
                        <Row>
                          <div className={classes.inputContainerMembership}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.aadvantage"/>
                            </label>
                            <div>
                              <AALogoSmall />
                              <Field
                                placeholder={intl.formatMessage(messages.aadvantagePlaceholder)}
                                type="text"
                                name="aadvantage"
                                id="aadvantage"
                                className={
                                  errors.aadvantage && touched.aadvantage
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                            </div>
                            <ErrorMessage
                              name="aadvantage"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>
                        <Row>
                          <Col xs={12} xl={6}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.firstName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.firstNamePlaceholder)}
                                type="text"
                                name="firstName"
                                id="firstName"
                                className={
                                  errors.firstName && touched.firstName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              <ErrorMessage
                                name="firstName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                          <Col xs={12} xl={6}>
                            <div className={classes.inputContainer}>
                              <label htmlFor="status" className={classes.label}>
                                <FormattedMessage id="traveler.details.lastName"/>
                              </label>
                              <Field
                                placeholder={intl.formatMessage(messages.lastNamePlaceholder)}
                                type="text"
                                name="lastName"
                                id="lastName"
                                className={
                                  errors.lastName && touched.lastName
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              <ErrorMessage
                                name="lastName"
                                component="span"
                                className={classes.error}
                              />
                            </div>
                          </Col>
                        </Row>
                        <Row>
                          <div className={classes.inputMailContainer}>
                            <label htmlFor="status" className={classes.label}>
                              <FormattedMessage id="traveler.details.emailAddress"/>
                            </label>
                            <div className={classes.editDiv}>
                              <Field
                                placeholder={intl.formatMessage(messages.emailPlaceholder)}
                                type="text"
                                name="emailAddress"
                                id="emailAddress"
                                className={
                                  errors.emailAddress && touched.emailAddress
                                    ? "input-error"
                                    : null
                                }
                                disabled={true}
                              />
                              {checkPermission("F_EDIT_MY_EMAILADDRESS" || "F_EDIT_OTHERS_EMAILADDRESS") &&
                                <OverlayTrigger
                                  placement="top"
                                  trigger={!travelerDetails?.allowEditEmail ? ['focus', 'click'] : []}
                                  overlay={
                                    <Tooltip id="email" className={`${general.main} ${classes.emailTooltip}`}>
                                      <div><FormattedMessage id="updateEmail.tooltip"/></div>
                                    </Tooltip>
                                  }
                                >
                                  <span>
                                    <UCButton
                                      onClick={() => setShowUpdateEmailModal(true)}
                                      className={classes.editButton}
                                      iconEnd={<EditEmailSvg/>}
                                      disabled={!travelerDetails?.allowEditEmail || travelerDetails?.userStatus === "REMOVED"}
                                    />
                                  </span>
                                </OverlayTrigger>
                              }
                            </div>
                            <ErrorMessage
                              name="emailAddress"
                              component="span"
                              className={classes.error}
                            />
                          </div>
                        </Row>
                        <div className={classes.checkboxContainer}>
                          <UCCheckbox
                            checked={checkboxValues.isTripLinkAccount}
                            onChange={() => handleCheckbox('isTripLinkAccount')}
                            label={<FormattedMessage id="traveler.details.triplink"/>}
                            className={classes.tripLink}
                          />                          
                          <OverlayTrigger
                            placement="top"
                            show={showTooltip}
                            overlay={
                              <Tooltip
                                id="tooltipRole"
                                className={`${general.main} ${classes.concurTooltip}`}
                                onMouseEnter={handleMouseEnter}
                                onMouseLeave={handleMouseLeave}
                              >
                                <FormattedMessage id="traveler.details.tripLinkTooltip" />
                                <Link to={getTripitLink(userDetails?.userInfo?.providerInfo?.providerCode)} target="_blank">
                                  <FormattedMessage id="traveler.details.tripLinkTooltip.link" />
                                </Link>
                              </Tooltip>
                            }
                          >
                            <HelpIcon
                              className={classes.helpIcon}
                              onMouseEnter={handleMouseEnter}
                              onMouseLeave={handleMouseLeave}
                            />
                          </OverlayTrigger>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  <div className="row">
                    <div className="col-12 col-xl-4">
                      <div className={classes.personalDetails}>
                        <FormattedMessage id="traveler.details.notification"/>
                      </div>
                      <div className={classes.description}>
                        <FormattedMessage id="traveler.details.notificationDescription"/>
                      </div>
                    </div>
                    <div className="col-12 col-md-8">
                      <div className={`${classes.formContainer} ${classes.containerMargin}`}>
                        {
                          travelerDetails?.showMarketingBusinessSummary && 
                          <>
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.business.title"/>}
                                description={<FormattedMessage id="traveler.details.business.description"/>}
                                checkboxChange={() => handleCheckbox('marketingBusinessSummary')}
                                checked={checkboxValues.marketingBusinessSummary}
                              />
                            </Row>
                          </>
                        }
                        {
                          travelerDetails?.showMarketingBusinessSummary && (travelerDetails?.showMarketingPromotions || travelerDetails?.showMarketingNewsAndOffers ) &&
                          <div className={classes.dividerContainer}>
                            <hr className={`${classes.border} ${classes.littleBorder}`}/>
                          </div>
                        } 
                        {
                          travelerDetails?.showMarketingPromotions && 
                          <>
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.promotions.title"/>}
                                description={<FormattedMessage id="traveler.details.promotions.description"/>}
                                checkboxChange={() => handleCheckbox('marketingPromotions')}
                                checked={checkboxValues.marketingPromotions}
                              />
                            </Row>
                          </>
                        }
                        {
                          travelerDetails?.showMarketingPromotions && travelerDetails?.showMarketingNewsAndOffers &&
                          <div className={classes.dividerContainer}>
                            <hr className={`${classes.border} ${classes.littleBorder}`}/>
                          </div>
                        }                        
                        { 
                          travelerDetails?.showMarketingNewsAndOffers && 
                          <>                          
                            <Row>
                              <NotificationPreference 
                                title={<FormattedMessage id="traveler.details.newsandoffers.title"/>}
                                description={<FormattedMessage id="traveler.details.newsandoffers.description"/>}
                                checkboxChange={() => handleCheckbox('marketingNewsAndOffers')}
                                checked={checkboxValues.marketingNewsAndOffers}
                              />
                            </Row>           
                          </>                        
                        }                          
                      </div>
                    </div>
                  </div>

                  <div className={classes.dividerContainer}>
                    <hr className={classes.border}/>
                  </div>

                  <Row className={classes.rowBtns}>
                    <Col className={classes.colBtn}>
                      <UCButton
                        type="deleteLink"
                        className={classes.leaveButton}
                        text={<FormattedMessage id="traveler.details.leaveCompany"/>}
                        onClick={() => travelerDetails?.authorizedUser ? setShowLeaveCompanyAuthUserModal(true) : setShowLeaveCompanyModal(true)}
                        icon={<DeleteUserIconRed />}
                      />
                    </Col>
                    <Col>
                      <div className={classes.buttonContainer}>
                        <div className={classes.btnContainer}>
                          <UCButton
                            type="default"
                            className={classes.button}
                            text={<FormattedMessage id="traveler.details.cancel"/>}
                            onClick={goBack}
                          />
                          {
                            (checkPermission("F_EDIT_USER") ||
                              (travelerDetails?.userEntityId === userDetails?.userInfo?.userEntityId && checkPermission("F_EDIT_MYUSER"))
                            ) &&
                            <UCButton
                              type="primary"
                              onClick={handleFormSubmit}
                              text={<FormattedMessage id="traveler.details.save"/>}
                              spinner={{
                                name: "updatePeopleSpinner",
                                size: 20,
                              }}
                            />
                          }
                        </div>
                      </div>
                    </Col>
                  </Row>
                </form>
              </>
            )
          }
          }
        </Formik>
      }
        </>
      )}
      <LeaveCompanyModal
        showModal={showLeaveCompanyModal}
        setShowModal={setShowLeaveCompanyModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="traveler.details.leaveCompany.title" values={{name: `${travelerDetails?.companyInfo?.companyName}`}}/> }
        modalDescription={ <FormattedMessage id="traveler.details.leaveCompany.description" /> }
        submitCallback={leaveAction}
      />

      <WarningModal
        showModal={showLeaveCompanyAuthUserModal}
        setShowModal={setShowLeaveCompanyAuthUserModal}
        modalIcon={ <UserDeleteIcon /> }
        modalTitle={ <FormattedMessage id="removeCitiUser.authUser.title"/> }
        modalDescription={ <FormattedMessage id="removeCitiOnlyModal.authUser.description" /> }
      />
      <UpdateEmailModal
        setShowUpdateEmailModal={setShowUpdateEmailModal}
        showUpdateEmailModal={showUpdateEmailModal}
        travelerDetails={ travelerDetails }
      />
    </div>
  )
}

export default ProfileTab;
